import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, Output, inject } from '@angular/core'
import { IonicModule, AlertController } from '@ionic/angular'
import { Router } from '@angular/router'
import { H2HPet } from 'src/app/types/pet'
import { PetService } from 'src/app/services/pet.service'
import { ToastService } from 'src/app/services/toast.service'
import { REMOVAL_REASONS } from 'src/app/constants/removal_reasons'
import { first } from 'rxjs'

@Component({
  selector: 'app-expired-pets-modal',
  templateUrl: './expired-pets-modal.component.html',
  styleUrls: ['./expired-pets-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule],
})
export class ExpiredPetsModalComponent {
  @Input() isOpen = false
  @Input() set expiredPets(pets: H2HPet[]) {
    // Sort pets by expiration date (oldest to newest)
    this._expiredPets = pets.sort((a, b) => {
      const dateA = a.expiresAt ? new Date(a.expiresAt).getTime() : 0
      const dateB = b.expiresAt ? new Date(b.expiresAt).getTime() : 0
      return dateA - dateB
    })
  }

  get expiredPets() {
    return this._expiredPets
  }

  private _expiredPets: H2HPet[] = []
  @Output() modalClosed = new EventEmitter<void>()

  private router = inject(Router)
  private petService = inject(PetService)
  private toastService = inject(ToastService)
  private alertController = inject(AlertController)

  REMOVAL_REASONS = REMOVAL_REASONS

  get petCount() {
    return this.expiredPets.length
  }

  get isPlural() {
    return this.petCount > 1
  }

  closeModal() {
    this.modalClosed.emit()
  }

  viewPetProfile(pet: H2HPet) {
    this.router.navigate(['/pets', pet.uuid])
    this.closeModal()
  }

  extendPet(pet: H2HPet) {
    this.router.navigate(['/pets', pet.uuid, 'edit'], {
      queryParams: { extendExpiration: 'true' },
    })
    this.closeModal()
  }

  async markOutcome(pet: H2HPet) {
    const alert = await this.alertController.create({
      header: `Remove Pet: ${pet.name}`,
      message: 'Why are you removing this pet from your listings?',
      inputs: Object.entries(this.REMOVAL_REASONS).map(([key, value]) => ({
        type: 'radio' as const,
        label: value,
        value: key,
      })),
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Remove Pet',
          handler: (selectedReason) => {
            if (selectedReason) {
              this.removePet(pet, selectedReason)
              this.closeModal() // Close the main modal after removing pet
            }
          },
        },
      ],
    })

    await alert.present()
  }

  private removePet(pet: H2HPet, reason: keyof typeof REMOVAL_REASONS) {
    this.petService
      .deletePet(pet.uuid, reason)
      .pipe(first())
      .subscribe({
        next: () => {
          this.toastService.presentToast({
            message: `${pet.name} has been removed`,
            color: 'success',
          })
          // Remove the pet from the local array
          this._expiredPets = this._expiredPets.filter(
            (p) => p.uuid !== pet.uuid,
          )

          // Close modal if no more expired pets
          if (this.expiredPets.length === 0) {
            this.closeModal()
          }
        },
        error: (error) => {
          console.error('Error removing pet:', error)
          this.toastService.presentToast({
            message: `Failed to remove ${pet.name}. Please try again.`,
            color: 'danger',
          })
        },
      })
  }
}
